import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { toEditorHtml } from './richTextUtils';

const DEFAULT_HEIGHT = 'auto';
const EMPTY_HTML = '<p><br></p>';

const TOOLBAR_OPTIONS = [
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'clean'],
];

const normalizeOutput = (html) => {
  const raw = String(html || '').trim();
  if (!raw || raw === EMPTY_HTML) return '';
  return raw;
};

const applyHtmlToQuill = (quill, htmlValue) => {
  const html = toEditorHtml(htmlValue);
  if (!html) {
    quill.setText('');
    return;
  }
  const delta = quill.clipboard.convert(html);
  quill.setContents(delta, 'silent');
};

function RichTextEditor({
  value = '',
  onChange,
  loadKey = 0,
  readOnly = false,
  placeholder = '',
  height = DEFAULT_HEIGHT,
  className = '',
}) {
  const wrapperRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const applyingRef = useRef(false);

  onChangeRef.current = onChange;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    // Quill inserts the toolbar as a sibling of the editor node, so clear the
    // whole wrapper on each init to avoid stacked toolbars on edit.
    wrapper.innerHTML = '';
    const editorNode = document.createElement('div');
    wrapper.appendChild(editorNode);

    const quill = new Quill(editorNode, {
      theme: 'snow',
      placeholder,
      readOnly,
      modules: {
        toolbar: TOOLBAR_OPTIONS,
      },
    });

    applyingRef.current = true;
    applyHtmlToQuill(quill, value);
    applyingRef.current = false;

    const handleTextChange = () => {
      if (applyingRef.current) return;
      onChangeRef.current?.(normalizeOutput(quill.root.innerHTML));
    };

    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      wrapper.innerHTML = '';
    };
    // Re-init only when loadKey changes; value is read from the render that bumped loadKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey, placeholder, readOnly]);

  return (
    <div className={`sk-rich-text-editor ${className}`.trim()} style={{ height }}>
      <div ref={wrapperRef} className="sk-rich-text-editor__wrapper" />
    </div>
  );
}

export default RichTextEditor;
